import React, { useMemo } from "react";

import PropTypes from "prop-types";

import { v4 as uuidv4 } from "uuid";

import { Table as MuiTable } from "@mui/material";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";

import SoftBox from "components/SoftBox";
import SoftAvatar from "components/SoftAvatar";
import SoftTypography from "components/SoftTypography";

import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";
import borders from "assets/theme/base/borders";
import { useNavigate, useLocation } from "react-router-dom";

function Table({ columns, rows }) {
  const { light } = colors;
  const { size } = typography;
  const { borderWidth } = borders;

  const navigate = useNavigate();
  const location = useLocation();

  const renderColumns = columns.map(({ name, align, width }, key) => {
    let pl;
    let pr;

    if (key === 0) {
      pl = 3;
      pr = 3;
    } else if (key === columns.length - 1) {
      pl = 3;
      pr = 3;
    } else {
      pl = 1;
      pr = 1;
    }

    return (
      <SoftBox
        key={name}
        component="th"
        width={width || "auto"}
        pt={1.25}
        pb={1.25}
        pl={align === "left" ? pl : 3}
        pr={align === "right" ? pr : 3}
        textAlign={align}
        fontSize={size.sm}
        fontWeight="medium"
        color="black"
        textTransform="capitalize"
        opacity={0.9}
        sx={{
          backgroundColor: "skyblue.main",
        }}
      >
        {name}
      </SoftBox>
    );
  });

  const renderRows = rows.map((row, key) => {
    const rowKey = `row-${key}`;
    const linkPath = row?.linkPath;
    const noLinkCells = row.noLinkCells || [];
    const rowBackgroundColor = (key + 1) % 2 === 0 ? "skyblue.main" : "white.main";

    const tableRow = columns.map(({ name, align }) => {
      let template;

      if (Array.isArray(row[name])) {
        template = (
          <SoftBox display="flex" alignItems="center" py={0.5} px={1}>
            <SoftBox mr={2}>
              <SoftAvatar src={row[name][0]} name={row[name][1]} variant="rounded" size="sm" />
            </SoftBox>
            <SoftTypography variant="button" fontWeight="medium" sx={{ width: "max-content" }}>
              {row[name][1]}
            </SoftTypography>
          </SoftBox>
        );
      } else {
        template = (
          <SoftTypography
            variant="button"
            fontWeight="regular"
            color="secondary"
            sx={{ display: "inline-block", width: "max-content" }}
          >
            {row[name]}
          </SoftTypography>
        );
      }

      return (
        <SoftBox
          key={uuidv4()}
          component="td"
          p={1}
          textAlign={align}
          borderBottom={row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null}
          onClick={noLinkCells.includes(name) ? (event) => event.stopPropagation() : undefined}
          sx={{
            backgroundColor: rowBackgroundColor,
            verticalAlign: "middle",
          }}
        >
          {template}
        </SoftBox>
      );
    });

    return (
      <TableRow
        key={rowKey}
        onClick={() => {
          if (linkPath) {
            navigate(linkPath, {
              state: {
                from: row.returnPath || `${location.pathname}${location.search}`,
                ...(row.isDeleted ? { isDeleted: true } : {}),
              },
            });
          }
        }}
        sx={{
          cursor: linkPath ? "pointer" : "default",
        }}
      >
        {tableRow}
      </TableRow>
    );
  });

  return useMemo(
    () => (
      <TableContainer>
        <MuiTable>
          <SoftBox component="thead">
            <TableRow>{renderColumns}</TableRow>
          </SoftBox>
          <TableBody>{renderRows}</TableBody>
        </MuiTable>
      </TableContainer>
    ),
    [columns, rows, navigate, location.pathname, location.search]
  );
}

Table.defaultProps = {
  columns: [],
  rows: [{}],
};

Table.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
};

export default Table;
